import { useEffect, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "@/components/Logo";
import { useDemo } from "@/context/DemoContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/features", label: "Features" },
  { to: "/lean-operations", label: "Lean Ops" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openDemo } = useDemo();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div
        className={`transition-colors duration-300 ${
          scrolled ? "border-b border-white/10 bg-background/70 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
        }`}
      >
        <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Logo />

          <div className="hidden items-center gap-1 md:flex">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    isActive ? "text-white" : "text-muted-foreground hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="hidden items-center gap-3 md:flex">
            <Link to="/admin/login" className="text-sm font-medium text-muted-foreground transition-colors hover:text-white" data-testid="nav-admin">
              Admin
            </Link>
            <button
              onClick={() => openDemo("demo")}
              data-testid="nav-book-demo"
              className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:scale-[1.04] hover:shadow-[0_10px_30px_-8px_rgba(16,185,129,0.6)]"
            >
              Book a Demo
            </button>
          </div>

          <button
            className="text-white md:hidden"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-b border-white/10 bg-background/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 text-base font-medium ${isActive ? "bg-secondary text-white" : "text-muted-foreground"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/admin/login" className="rounded-xl px-4 py-3 text-base font-medium text-muted-foreground">Admin</Link>
              <button
                onClick={() => openDemo("demo")}
                className="mt-2 rounded-full bg-primary px-5 py-3 text-base font-semibold text-primary-foreground"
                data-testid="nav-mobile-book-demo"
              >
                Book a Demo
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
